import React, { useState } from "react";
import moment from "moment";
import AnalysisReading from "./AnalysisReading";
import { humanise } from "../constants";

// The last few readings of one photograph, and what changed between them.
//
// WHY THIS EXISTS. Re-analysing overwrites the photo's labels - that is right for every consumer of a label,
// and it makes the only question worth asking about a second model impossible to answer: "did it read OUR
// photographs better?" The backend keeps the newest few readings (3), and this is where they are compared.
//
// A SWITCHER, not a side-by-side. The tab is a column inside the image viewer, roughly 400px wide; two
// readings shown at once would be two unreadable columns. So one reading is displayed at a time, and what
// would be lost by switching - what actually DIFFERS from the latest - is stated above it in words. That
// summary is the comparison; the full reading below it is the detail.
//
// The latest reading is the photo's current labels. Any other is shown with that said plainly, because a
// curator reading an older entry and assuming it is what the library holds is the one way this misleads.

const FIELDS = [
    ["primary_subject", "Primary subject"],
    ["setting", "Setting"],
    ["space_type", "Space"],
    ["quality", "Quality"]
];

const names = analysis => (analysis && analysis.objects ? analysis.objects.map(o => o.name) : []);
const defectKinds = analysis => (analysis && analysis.defects ? analysis.defects.map(d => d.kind) : []);
const texts = analysis => (analysis && analysis.visible_text ? analysis.visible_text.map(t => t.text) : []);

/** What changed between an older reading and the current one, in the terms the page shows. */
export function describeDifferences(older, latest) {
    if (!older || !latest) return [];
    const out = [];
    FIELDS.forEach(([key, label]) => {
        if (older[key] !== latest[key]) {
            out.push(`${label}: ${humanise(older[key]) || "none"} to ${humanise(latest[key]) || "none"}`);
        }
    });

    const wasObjects = names(older);
    const nowObjects = names(latest);
    const added = nowObjects.filter(n => !wasObjects.includes(n));
    const dropped = wasObjects.filter(n => !nowObjects.includes(n));
    if (added.length) out.push(`Objects added: ${added.join(", ")}`);
    if (dropped.length) out.push(`Objects no longer seen: ${dropped.join(", ")}`);

    const wasDefects = defectKinds(older);
    const nowDefects = defectKinds(latest);
    const addedDefects = nowDefects.filter(n => !wasDefects.includes(n));
    const droppedDefects = wasDefects.filter(n => !nowDefects.includes(n));
    if (addedDefects.length) out.push(`Deficiencies added: ${addedDefects.join(", ")}`);
    if (droppedDefects.length) out.push(`Deficiencies no longer seen: ${droppedDefects.join(", ")}`);

    // What each model could READ off the same photograph is one of the sharper differences between two
    // readings - and the one a curator can check against the picture in front of them without expertise.
    const wasText = texts(older);
    const nowText = texts(latest);
    const addedText = nowText.filter(t => !wasText.includes(t));
    const droppedText = wasText.filter(t => !nowText.includes(t));
    if (addedText.length) out.push(`Text now read: ${addedText.map(t => `"${t}"`).join(", ")}`);
    if (droppedText.length) out.push(`Text no longer read: ${droppedText.map(t => `"${t}"`).join(", ")}`);

    return out;
}

const when = value => (value ? moment(value).format("MM-DD-YYYY hh:mm A") : "");

function ReadingHistory({ history = [], showPricing = false, modelLabel = id => id }) {
    const [index, setIndex] = useState(0);
    if (!history.length) return null;

    const latest = history[0];
    const shown = history[Math.min(index, history.length - 1)];
    const isLatest = shown === latest;
    const differences = isLatest ? [] : describeDifferences(shown.analysis, latest.analysis);

    return (
        <div className="ia-history">
            {history.length > 1 && (
                <>
                    <div className="ia-section-label">Readings ({history.length} kept)</div>
                    <div className="ia-history-tabs">
                        {history.map((reading, i) => (
                            <button
                                key={reading.id || i}
                                type="button"
                                className={`ia-history-tab ${i === index ? "ia-history-tab-on" : ""}`}
                                onClick={() => setIndex(i)}
                                title={when(reading.analysed_at)}
                            >
                                {i === 0 ? "Latest" : `Reading ${history.length - i}`}
                                <span className="ia-history-model">{modelLabel(reading.model)}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}

            {!isLatest && (
                <div className="ia-history-older">
                    An earlier reading. The image&apos;s current labels come from the latest one.
                    {differences.length ? (
                        <ul className="ia-history-diff">
                            {differences.map(line => (
                                <li key={line}>{line}</li>
                            ))}
                        </ul>
                    ) : (
                        <div className="ia-history-diff-same">Nothing changed between this reading and the latest.</div>
                    )}
                </div>
            )}

            <AnalysisReading analysis={shown.analysis} />

            <div className="ia-meta">
                Analysed {when(shown.analysed_at)}
                {shown.model ? ` · ${modelLabel(shown.model)}` : ""}
                {typeof shown.cost_usd === "number" && showPricing ? ` · $${shown.cost_usd.toFixed(4)}` : ""}
                {shown.is_current_version === false ? " · analysed with an earlier version" : ""}
            </div>
        </div>
    );
}

export default ReadingHistory;
